import { philippinesPsgcData } from '../data/philippinesPsgcData';

const NCR_REGION_CODE = '130000000';
const ISLAND_GROUPS = [
    { value: 'Luzon', regionCodes: ['010000000', '020000000', '030000000', '040000000', '050000000', '130000000', '140000000', '170000000'] },
    { value: 'Visayas', regionCodes: ['060000000', '070000000', '080000000'] },
    { value: 'Mindanao', regionCodes: ['090000000', '100000000', '110000000', '120000000', '150000000', '160000000'] },
];
const ISLAND_GROUP_BY_REGION_CODE = ISLAND_GROUPS.reduce((accumulator, group) => {
    group.regionCodes.forEach((regionCode) => {
        accumulator[regionCode] = group.value;
    });
    return accumulator;
}, {});
const METRO_MANILA_COVERAGE_OPTIONS = [
    { value: 'metro-manila', label: 'Metro Manila' },
    { value: 'taguig-only', label: 'Taguig City only' },
];
const DELIVERY_COVERAGE_TYPE_OPTIONS = [
    { value: 'all', label: 'All areas' },
    { value: 'island_group', label: 'Island group (Luzon / Visayas / Mindanao)' },
    { value: 'region', label: 'Specific region' },
    { value: 'province', label: 'Specific province' },
    { value: 'city', label: 'Specific city / municipality' },
    { value: 'barangay', label: 'Specific barangay' },
];

let locationCachePromise = null;

function ensureArray(value) {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.value)) return value.value;
    if (Array.isArray(value?.Value)) return value.Value;
    return [];
}

function repairText(value) {
    const text = String(value || '');
    if (!text) return '';
    if (!/[ÃÂâ]/.test(text)) return text;

    try {
        const bytes = Uint8Array.from(Array.from(text, (character) => character.charCodeAt(0) & 0xff));
        return new TextDecoder('utf-8').decode(bytes);
    } catch {
        return text;
    }
}

function toTitleCase(value) {
    return repairText(value)
        .toLowerCase()
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function normalizeValue(value) {
    return repairText(value)
        .toLowerCase()
        .replace(/city of\s+/g, '')
        .replace(/\s+city\b/g, '')
        .replace(/municipality of\s+/g, '')
        .replace(/province of\s+/g, '')
        .replace(/national capital region/g, 'metromanila')
        .replace(/ncr/g, 'metromanila')
        .replace(/[^a-z0-9]/g, '');
}

function formatMunicipalityName(name) {
    const raw = repairText(name).trim();
    if (!raw) return '';
    if (/^city of /i.test(raw)) {
        return `${raw.replace(/^city of /i, '').trim()} City`;
    }
    return raw;
}

function createLookupMap(items, codeKey) {
    return items.reduce((accumulator, item) => {
        accumulator[item[codeKey]] = item;
        return accumulator;
    }, {});
}

function joinLocationParts(...parts) {
    return parts.reduce((accumulator, part) => {
        const text = repairText(part).trim();
        if (!text) return accumulator;
        if (accumulator.some((existing) => normalizeValue(existing) === normalizeValue(text))) {
            return accumulator;
        }
        accumulator.push(text);
        return accumulator;
    }, []).join(', ');
}

function createCoverageOption(type, label, extra = {}) {
    return {
        type,
        value: label,
        label,
        normalized: normalizeValue(label),
        ...extra,
    };
}

function getIslandGroupByRegionCode(regionCode = '') {
    return ISLAND_GROUP_BY_REGION_CODE[regionCode] || '';
}

export function normalizeCoverageMode(value) {
    return METRO_MANILA_COVERAGE_OPTIONS.some((option) => option.value === value)
        ? value
        : 'metro-manila';
}

export function getMetroManilaCoverageLabel(value) {
    return METRO_MANILA_COVERAGE_OPTIONS.find((option) => option.value === value)?.label || 'Metro Manila';
}

export function getMetroManilaCoverageOptions() {
    return METRO_MANILA_COVERAGE_OPTIONS;
}

export function normalizeDeliveryCoverageType(value) {
    return DELIVERY_COVERAGE_TYPE_OPTIONS.some((option) => option.value === value)
        ? value
        : 'all';
}

export function getDeliveryCoverageTypeOptions() {
    return DELIVERY_COVERAGE_TYPE_OPTIONS;
}

export function normalizePhilippinesAddress(address = {}) {
    const region = String(address.region || '').trim();
    const province = String(address.province || '').trim();
    const normalizedRegion = normalizeValue(region || province);
    const isMetroManila = normalizedRegion === normalizeValue('Metro Manila');

    return {
        ...address,
        region: region || (isMetroManila ? 'Metro Manila' : ''),
        province: isMetroManila ? 'Metro Manila' : province,
    };
}

export async function loadMetroManilaLocationData() {
    return loadPhilippinesLocationData();
}

export async function loadPhilippinesLocationData() {
    if (!locationCachePromise) {
        locationCachePromise = Promise.resolve().then(() => {
            const regions = ensureArray(philippinesPsgcData.regions).map((region) => ({
                code: region.reg_code,
                name: repairText(region.name),
                label: region.reg_code === NCR_REGION_CODE ? 'Metro Manila' : repairText(region.name),
                normalized: normalizeValue(region.reg_code === NCR_REGION_CODE ? 'Metro Manila' : repairText(region.name)),
            }));

            const rawProvinces = ensureArray(philippinesPsgcData.provinces).map((province) => ({
                code: province.prv_code,
                regionCode: province.reg_code,
                name: repairText(province.name),
            }));

            const rawProvinceByCode = createLookupMap(rawProvinces, 'code');
            const provinceMap = new Map();
            rawProvinces.forEach((province) => {
                const code = province.regionCode === NCR_REGION_CODE ? NCR_REGION_CODE : province.code;
                const label = province.regionCode === NCR_REGION_CODE ? 'Metro Manila' : province.name;
                if (!provinceMap.has(code)) {
                    provinceMap.set(code, {
                        code,
                        regionCode: province.regionCode,
                        name: label,
                        label,
                        normalized: normalizeValue(label),
                        sourceCodes: [],
                    });
                }
                provinceMap.get(code).sourceCodes.push(province.code);
            });
            const provinces = Array.from(provinceMap.values());

            const regionByCode = createLookupMap(regions, 'code');
            const provinceByCode = createLookupMap(provinces, 'code');

            const provincesByRegionCode = provinces.reduce((accumulator, province) => {
                if (!accumulator[province.regionCode]) accumulator[province.regionCode] = [];
                if (!accumulator[province.regionCode].some((entry) => entry.code === province.code)) {
                    accumulator[province.regionCode].push(province);
                }
                return accumulator;
            }, {});

            Object.values(provincesByRegionCode).forEach((items) => items.sort((a, b) => a.label.localeCompare(b.label)));

            const municipalities = ensureArray(philippinesPsgcData.municipalities).map((municipality) => {
                const rawProvince = rawProvinceByCode[municipality.prv_code];
                const provinceCode = rawProvince?.regionCode === NCR_REGION_CODE ? NCR_REGION_CODE : municipality.prv_code;
                const province = provinceByCode[provinceCode];
                const region = province ? regionByCode[province.regionCode] : null;
                const label = formatMunicipalityName(municipality.name);
                return {
                    code: municipality.mun_code,
                    provinceCode,
                    regionCode: region?.code || '',
                    name: repairText(municipality.name),
                    label,
                    normalized: normalizeValue(label),
                };
            });

            const municipalitiesByProvinceCode = municipalities.reduce((accumulator, municipality) => {
                if (!accumulator[municipality.provinceCode]) accumulator[municipality.provinceCode] = [];
                accumulator[municipality.provinceCode].push(municipality);
                return accumulator;
            }, {});

            Object.values(municipalitiesByProvinceCode).forEach((items) => items.sort((a, b) => a.label.localeCompare(b.label)));
            const municipalityByCode = createLookupMap(municipalities, 'code');

            const barangays = ensureArray(philippinesPsgcData.barangays).map((barangay) => ({
                code: barangay.bgy_code,
                municipalityCode: barangay.mun_code,
                name: repairText(barangay.name),
                label: toTitleCase(barangay.name),
                normalized: normalizeValue(barangay.name),
            }));

            const barangaysByMunicipalityCode = barangays.reduce((accumulator, barangay) => {
                if (!accumulator[barangay.municipalityCode]) accumulator[barangay.municipalityCode] = [];
                accumulator[barangay.municipalityCode].push(barangay);
                return accumulator;
            }, {});

            Object.values(barangaysByMunicipalityCode).forEach((items) => items.sort((a, b) => a.label.localeCompare(b.label)));

            const coverageOptionsByType = {
                all: [createCoverageOption('all', 'All areas')],
                island_group: ISLAND_GROUPS.map((group) => createCoverageOption('island_group', group.value)),
                region: regions.map((region) => createCoverageOption('region', region.label, { code: region.code })),
                province: provinces.map((province) => createCoverageOption(
                    'province',
                    joinLocationParts(province.label, regionByCode[province.regionCode]?.label),
                    { code: province.code, regionCode: province.regionCode }
                )),
                city: municipalities.map((municipality) => createCoverageOption(
                    'city',
                    joinLocationParts(
                        municipality.label,
                        provinceByCode[municipality.provinceCode]?.label,
                        regionByCode[municipality.regionCode]?.label
                    ),
                    { code: municipality.code, provinceCode: municipality.provinceCode, regionCode: municipality.regionCode }
                )),
                barangay: barangays.map((barangay) => {
                    const municipality = municipalityByCode[barangay.municipalityCode];
                    const province = municipality ? provinceByCode[municipality.provinceCode] : null;
                    const region = municipality ? regionByCode[municipality.regionCode] : null;
                    return createCoverageOption(
                        'barangay',
                        joinLocationParts(barangay.label, municipality?.label, province?.label, region?.label),
                        {
                            code: barangay.code,
                            municipalityCode: barangay.municipalityCode,
                            provinceCode: municipality?.provinceCode || '',
                            regionCode: municipality?.regionCode || '',
                        }
                    );
                }),
            };

            Object.values(coverageOptionsByType).forEach((items) => items.sort((a, b) => a.label.localeCompare(b.label)));

            return {
                regions,
                provinces,
                municipalities,
                barangays,
                regionByCode,
                provinceByCode,
                municipalitiesByProvinceCode,
                barangaysByMunicipalityCode,
                provincesByRegionCode,
                coverageOptionsByType,
            };
        });
    }

    return locationCachePromise;
}

export async function getPhilippinesRegions() {
    const data = await loadPhilippinesLocationData();
    return data.regions;
}

export async function getPhilippinesProvinces(regionName) {
    const data = await loadPhilippinesLocationData();
    const region = data.regions.find((entry) => entry.normalized === normalizeValue(regionName));
    return region ? (data.provincesByRegionCode[region.code] || []) : [];
}

export async function getPhilippinesCities(regionName, provinceName) {
    const data = await loadPhilippinesLocationData();
    const region = data.regions.find((entry) => entry.normalized === normalizeValue(regionName));
    if (!region) return [];

    const province = (data.provincesByRegionCode[region.code] || []).find(
        (entry) => entry.normalized === normalizeValue(provinceName || (region.code === NCR_REGION_CODE ? 'Metro Manila' : ''))
    );
    return province ? (data.municipalitiesByProvinceCode[province.code] || []) : [];
}

export async function getPhilippinesBarangays(regionName, provinceName, cityName) {
    const data = await loadPhilippinesLocationData();
    const cities = await getPhilippinesCities(regionName, provinceName);
    const city = cities.find((entry) => entry.normalized === normalizeValue(cityName));
    return city ? (data.barangaysByMunicipalityCode[city.code] || []) : [];
}

export async function getMetroManilaCities() {
    return getPhilippinesCities('Metro Manila', 'Metro Manila');
}

export async function getMetroManilaBarangays(cityName) {
    return getPhilippinesBarangays('Metro Manila', 'Metro Manila', cityName);
}

export async function getDeliveryCoverageLocationOptions(type = 'city') {
    const data = await loadPhilippinesLocationData();
    return data.coverageOptionsByType[normalizeDeliveryCoverageType(type)] || [];
}

export function getDeliveryCoverageLabel(coverageType = 'all', coverageValue = '') {
    const normalizedType = normalizeDeliveryCoverageType(coverageType);
    if (normalizedType === 'all' || !String(coverageValue || '').trim()) {
        return 'All areas';
    }
    return repairText(coverageValue).trim();
}

export async function isAddressWithinDeliveryCoverage(address, coverageType = 'all', coverageValue = '') {
    const normalizedType = normalizeDeliveryCoverageType(coverageType);
    const normalizedValue = normalizeValue(coverageValue);
    if (normalizedType === 'all' || !normalizedValue) return true;

    const data = normalizedType === 'island_group'
        ? await loadPhilippinesLocationData()
        : null;
    const normalizedAddress = normalizePhilippinesAddress(address);
    const region = repairText(normalizedAddress.region || normalizedAddress.province || '').trim();
    const province = repairText(normalizedAddress.province || '').trim();
    const city = repairText(normalizedAddress.city || '').trim();
    const barangay = repairText(normalizedAddress.barangay || '').trim();
    const matchedRegion = data?.regions.find((entry) => entry.normalized === normalizeValue(region || province));
    const islandGroup = matchedRegion ? getIslandGroupByRegionCode(matchedRegion.code) : '';

    const regionPath = joinLocationParts(region);
    const provincePath = joinLocationParts(province, region);
    const cityPath = joinLocationParts(city, province, region);
    const barangayPath = joinLocationParts(barangay, city, province, region);

    const candidatesByType = {
        island_group: [islandGroup],
        region: [region, regionPath],
        province: [province, provincePath],
        city: [city, cityPath],
        barangay: [barangay, barangayPath],
    };

    return (candidatesByType[normalizedType] || []).some((candidate) => normalizeValue(candidate) === normalizedValue);
}

export async function isCoveredMetroManilaAddress(address, coverageMode = 'metro-manila') {
    const normalized = normalizePhilippinesAddress(address);
    const region = String(normalized.region || normalized.province || '').trim();
    const city = String(normalized.city || '').trim();
    const barangay = String(normalized.barangay || '').trim();

    if (normalizeValue(region) !== normalizeValue('Metro Manila')) return false;

    const cities = await getMetroManilaCities();
    const cityMatch = cities.find((entry) => entry.normalized === normalizeValue(city));
    if (!cityMatch) return false;

    if (normalizeCoverageMode(coverageMode) === 'taguig-only' && cityMatch.normalized !== normalizeValue('Taguig City')) {
        return false;
    }

    const barangays = await getMetroManilaBarangays(city);
    return barangays.some((entry) => entry.normalized === normalizeValue(barangay));
}

export function toMetroManilaAddress(address) {
    return {
        ...address,
        region: 'Metro Manila',
        province: 'Metro Manila',
    };
}